<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import FullCalendar from '@fullcalendar/vue3';
import dayGridPlugin from '@fullcalendar/vue3/daygrid';
import timeGridPlugin from '@fullcalendar/vue3/timegrid';
import listPlugin from '@fullcalendar/vue3/list';
import interactionPlugin from '@fullcalendar/vue3/interaction';
import classicThemePlugin from '@fullcalendar/vue3/themes/classic';
import ruLocale from '@fullcalendar/vue3/locales/ru';
import '@fullcalendar/vue3/skeleton.css';
import '@fullcalendar/vue3/themes/classic/theme.css';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import { calendarDate, calendarViews } from './model';

const props = defineProps({
  events: { type: Array, default: () => [] },
  view: { type: String, default: 'month' },
  date: { type: String, required: true },
  loading: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  timezone: { type: String, default: 'Europe/Moscow' },
});
const emit = defineEmits([
  'rangeChange',
  'viewChange',
  'eventClick',
  'deleteRun',
  'slotClick',
  'create',
]);

const calendar = ref(null);
const title = ref('');
const dayDialog = ref(null);
const expandedDate = ref('');
const showCancelled = ref(false);
const visibleEvents = computed(() =>
  props.events.filter(
    event =>
      showCancelled.value || event.extendedProps?.run?.state !== 'cancelled'
  )
);
const cancelledCount = computed(
  () =>
    props.events.length -
    props.events.filter(
      event => event.extendedProps?.run?.state !== 'cancelled'
    ).length
);
const expandedEvents = computed(() =>
  visibleEvents.value
    .filter(
      event =>
        calendarDate(new Date(event.start), props.timezone) ===
        expandedDate.value
    )
    .slice()
    .sort(
      (a, b) =>
        new Date(a.start) - new Date(b.start) ||
        String(a.id).localeCompare(String(b.id))
    )
);
const expandedTitle = computed(() =>
  expandedDate.value
    ? new Intl.DateTimeFormat('ru-RU', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        timeZone: 'UTC',
      }).format(new Date(`${expandedDate.value}T12:00:00Z`))
    : ''
);

function openDay(info) {
  expandedDate.value = calendarDate(info.date, props.timezone);
  dayDialog.value?.open();
  // Pinned FullCalendar 7: a truthy non-string result suppresses its body portal.
  // Use the native Chatwoot dialog instead; verify this with the real-calendar fixture.
  return true;
}

function openDayEvent(event) {
  dayDialog.value?.close();
  emit('eventClick', event.extendedProps.run);
}

function eventTime(event) {
  return new Intl.DateTimeFormat('ru-RU', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: props.timezone,
  }).format(new Date(event.start));
}
const compact = ref(
  typeof window !== 'undefined' &&
    window.matchMedia('(max-width: 639px)').matches
);
let media = null;

const actualView = view => {
  if (compact.value && view === 'month') return 'listMonth';
  if (compact.value && view === 'week') return 'listWeek';
  if (view === 'day') return 'timeGridDay';
  if (view === 'week') return 'timeGridWeek';
  return 'dayGridMonth';
};
const logicalView = name => {
  if (name.includes('Day')) return 'day';
  if (name.includes('Week')) return 'week';
  return 'month';
};
const api = () => calendar.value?.getApi();

function emitRange(info) {
  title.value = info.view.title;
  emit('rangeChange', {
    start: info.start.toISOString(),
    end: info.end.toISOString(),
    date: calendarDate(info.view.calendar.getDate(), props.timezone),
    view: logicalView(info.view.type),
  });
}

function changeView(view) {
  api()?.changeView(actualView(view));
  emit('viewChange', view);
}

function move(direction) {
  const instance = api();
  if (!instance) return;
  if (direction === 'today') instance.today();
  else if (direction === 'next') instance.next();
  else instance.prev();
}

const options = computed(() => ({
  plugins: [
    classicThemePlugin,
    dayGridPlugin,
    timeGridPlugin,
    listPlugin,
    interactionPlugin,
  ],
  locale: ruLocale,
  initialView: actualView(props.view),
  initialDate: props.date,
  timeZone: props.timezone,
  firstDay: 1,
  headerToolbar: false,
  height: 'auto',
  nowIndicator: true,
  allDaySlot: false,
  views: {
    dayGridMonth: {
      dayMaxEvents: false,
      dayCellInnerClass: 'tg-calendar-day-posts',
    },
  },
  moreLinkClick: openDay,
  eventDisplay: 'block',
  eventTimeFormat: { hour: '2-digit', minute: '2-digit', hour12: false },
  slotMinTime: '06:00:00',
  slotMaxTime: '24:00:00',
  slotDuration: '00:30:00',
  editable: false,
  selectable: false,
  navLinks: false,
  events: visibleEvents.value,
  datesSet: emitRange,
  dateClick: info =>
    emit('slotClick', {
      date: info.date,
      dateStr: info.dateStr,
      allDay: info.allDay,
    }),
  eventClick: info => emit('eventClick', info.event.extendedProps.run),
}));

watch(
  () => props.view,
  value => {
    const instance = api();
    if (instance && logicalView(instance.view.type) !== value)
      instance.changeView(actualView(value));
  }
);
watch(
  () => props.date,
  value => {
    const instance = api();
    if (
      instance &&
      value &&
      calendarDate(instance.getDate(), props.timezone) !== value
    )
      instance.gotoDate(value);
  }
);

function updateCompact(event) {
  const next = event.matches;
  if (next === compact.value) return;
  compact.value = next;
  const instance = api();
  if (instance) instance.changeView(actualView(props.view), instance.getDate());
}

onMounted(() => {
  media = window.matchMedia('(max-width: 639px)');
  compact.value = media.matches;
  media.addEventListener('change', updateCompact);
  const instance = api();
  if (instance && instance.view.type !== actualView(props.view))
    instance.changeView(actualView(props.view), props.date);
});
onBeforeUnmount(() => media?.removeEventListener('change', updateCompact));
</script>

<template>
  <section
    class="tg-publication-calendar min-w-0"
    data-feature="tg_publication_calendar_fullcalendar"
    aria-label="Календарь публикаций группы"
    :aria-busy="loading"
  >
    <div class="mb-4 flex flex-wrap items-center justify-between gap-3">
      <div class="flex min-w-0 items-center gap-2">
        <Button
          icon="i-lucide-chevron-left"
          title="Предыдущий период"
          aria-label="Предыдущий период"
          slate
          faded
          @click="move('previous')"
        />
        <Button
          label="Сегодня"
          slate
          faded
          class="hidden sm:inline-flex"
          @click="move('today')"
        />
        <Button
          icon="i-lucide-chevron-right"
          title="Следующий период"
          aria-label="Следующий период"
          slate
          faded
          @click="move('next')"
        />
        <h2
          class="m-0 truncate text-base font-medium capitalize text-n-slate-12 sm:text-lg"
        >
          {{ title }}
        </h2>
      </div>
      <div class="flex flex-wrap items-center gap-2">
        <Button
          v-for="item in calendarViews"
          :key="item.key"
          :label="item.label"
          :slate="view !== item.key"
          :faded="view !== item.key"
          :aria-pressed="view === item.key"
          @click="changeView(item.key)"
        />
        <Button
          label="Запланировать"
          icon="i-lucide-calendar-plus"
          @click="emit('create')"
        />
      </div>
    </div>
    <p v-if="loading" class="mb-3 text-sm text-n-slate-11" role="status">
      Обновляем календарь…
    </p>
    <label
      v-if="cancelledCount"
      class="mb-3 flex items-center gap-2 text-sm text-n-slate-11"
    >
      <input v-model="showCancelled" type="checkbox" />
      Показать отменённые ({{ cancelledCount }})
    </label>
    <p
      v-if="cancelledCount && !visibleEvents.length"
      class="mb-3 text-sm text-n-slate-11"
      role="status"
    >
      Все публикации этого периода отменены. Историю можно показать
      переключателем выше.
    </p>
    <FullCalendar ref="calendar" :options="options">
      <template #eventContent="info">
        <div class="relative min-w-0 px-1 py-0.5 text-left">
          <button
            v-if="info.event.extendedProps.run?.state === 'scheduled'"
            type="button"
            class="absolute right-0 top-0 z-10 flex size-5 !p-0 items-center justify-center rounded hover:bg-n-alpha-3 focus-visible:outline focus-visible:outline-2 focus-visible:outline-current"
            aria-label="Удалить из расписания"
            title="Удалить из расписания"
            :disabled="disabled"
            @click.stop.prevent="
              emit('deleteRun', info.event.extendedProps.run)
            "
            @keydown.enter.stop
            @keydown.space.stop
          >
            <span class="i-lucide-x size-3 shrink-0" aria-hidden="true" />
          </button>
          <div
            v-if="info.event.extendedProps.sourceName"
            class="flex min-w-0 items-center gap-1 mb-0.5 pr-5"
          >
            <Avatar
              :src="info.event.extendedProps.sourceAvatar"
              :name="info.event.extendedProps.sourceName"
              :size="16"
            />
            <span class="truncate text-[11px]">
              {{ info.event.extendedProps.sourceName }}
              {{
                info.event.extendedProps.sourceUsername
                  ? `· @${info.event.extendedProps.sourceUsername}`
                  : ''
              }}
            </span>
          </div>
          <p class="m-0 truncate text-xs font-medium">
            <span v-if="info.timeText">{{ info.timeText }} · </span
            >{{ info.event.title }}
          </p>
          <p class="m-0 truncate text-[11px] opacity-90">
            {{ info.event.extendedProps.status }}
          </p>
        </div>
      </template>
    </FullCalendar>
    <Dialog
      ref="dayDialog"
      :title="expandedTitle"
      description="Публикации выбранного дня"
      cancel-button-label="Закрыть"
      :show-confirm-button="false"
      overflow-y-auto
      width="lg"
    >
      <div
        class="min-w-0 max-h-[60vh] overflow-y-auto overflow-x-hidden flex flex-col gap-2"
      >
        <p
          v-if="!expandedEvents.length"
          class="m-0 text-sm text-n-slate-11"
          role="status"
        >
          В этот день нет публикаций для отображения.
        </p>
        <button
          v-for="event in expandedEvents"
          :key="event.id"
          type="button"
          class="w-full min-w-0 rounded-lg border border-n-weak bg-n-alpha-1 p-3 text-left text-sm text-n-slate-12 hover:bg-n-alpha-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-blue-9"
          @click="openDayEvent(event)"
        >
          <span class="flex min-w-0 items-center gap-2 mb-1">
            <Avatar
              :src="event.extendedProps.sourceAvatar"
              :name="event.extendedProps.sourceName"
              :size="24"
            />
            <span class="min-w-0 whitespace-normal [overflow-wrap:anywhere]">
              {{ event.extendedProps.sourceName }}
              {{
                event.extendedProps.sourceUsername
                  ? `· @${event.extendedProps.sourceUsername}`
                  : ''
              }}
            </span>
          </span>
          <span
            class="block whitespace-normal [overflow-wrap:anywhere] font-medium"
            >{{ eventTime(event) }} · {{ event.title }}</span
          >
          <span class="block mt-1 text-xs text-n-slate-11">{{
            event.extendedProps.status
          }}</span>
        </button>
      </div>
    </Dialog>
  </section>
</template>

<style scoped>
.tg-publication-calendar {
  --fc-classic-button: #334155;
  --fc-classic-button-border: #334155;
  --fc-classic-button-strong: #1e293b;
  --fc-classic-button-strong-border: #0f172a;
  --fc-classic-button-outline: #47556980;
  --fc-classic-button-foreground: #ffffff;
  --fc-classic-primary: #2563eb;
  --fc-classic-primary-foreground: #ffffff;
  --fc-classic-event: var(--fc-classic-primary);
  --fc-classic-event-contrast: var(--fc-classic-primary-foreground);
  --fc-classic-background-event: #22c55e;
  --fc-classic-background-event-opacity: 15%;
  --fc-classic-background-event-foreground-opacity: 50%;
  --fc-classic-highlight: #dbeafe;
  --fc-classic-background: #ffffff;
  --fc-classic-faint: #f8fafc;
  --fc-classic-muted: #f1f5f9;
  --fc-classic-strong: #e2e8f0;
  --fc-classic-foreground: #0f172a;
  --fc-classic-faint-foreground: #94a3b8;
  --fc-classic-muted-foreground: #64748b;
  --fc-classic-border: #e2e8f0;
  --fc-classic-strong-border: #cbd5e1;
  --fc-classic-today: #eff6ff;
  --fc-classic-now: #dc2626;
  --fc-classic-small-dot-width: 8px;
  --fc-classic-large-dot-width: 10px;
}

:deep(.fc) {
  min-width: 0;
  font-family: inherit;
  font-size: 0.875rem;
}

:deep(.fc a),
:deep(.fc button) {
  font-family: inherit;
}

/* FullCalendar 7's day body contains the measured event canvas. Scroll the
   body, preserving the canvas height and absolute event positions inside it.
   The date header is its sibling and therefore stays visible. */
:deep(div:has(> .tg-calendar-day-posts)) {
  height: 14rem;
  max-height: 14rem;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  overscroll-behavior-y: contain;
  scrollbar-gutter: stable;
}

@media print {
  :deep(div:has(> .tg-calendar-day-posts)) {
    height: auto;
    max-height: none;
    overflow: visible;
  }
}

@media (max-width: 639px) {
  :deep(.fc) {
    font-size: 0.8125rem;
  }
}
</style>
